/**
 * Deep Scan Modal (Pro-tier feature)
 *
 * Two-phase workflow with a single "Start" click:
 *   1. Kick off `/box/videos/{id}/deep-scan` → server spawns ai_deep_worker
 *   2. Poll `/box/videos/{id}/deep-scan-status` until the ai_job is done
 *   3. Auto-call `/box/videos/{id}/generate-all-clips` → server spawns clip_worker
 *   4. Poll `/clip-jobs/{id}` until every cut is rendered as a client clip
 *   5. Close and refresh the parent's clip list
 *
 * The whole thing looks like one progress bar to the user. On any failure
 * we surface a clear error and let them retry from the same button.
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Sparkles, Loader2, Check, AlertTriangle } from "lucide-react";

export default function DeepScanModal({ video, onClose, onDone }) {
  // "idle" → "scanning" → "generating" → "done" | "error"
  const [phase, setPhase] = useState("idle");
  const [error, setError] = useState("");
  const [scanJob, setScanJob] = useState(null);
  const [clipJob, setClipJob] = useState(null);
  const [cutsCount, setCutsCount] = useState(0);
  const cancelledRef = useRef(false);

  useEffect(() => () => { cancelledRef.current = true; }, []);

  const start = async () => {
    setError("");
    setPhase("scanning");
    try {
      const r = await api.post(`/box/videos/${video.id}/deep-scan`, { window_seconds: 600 });
      const jobId = r.data.job_id;
      // Phase 1 polling: ai_job status
      const scan = await pollAiJob(video.id, jobId, (job, cuts) => {
        setScanJob(job);
        setCutsCount(cuts);
      });
      if (cancelledRef.current) return;
      if (scan.status !== "completed") {
        throw new Error(scan.error || "Deep scan didn't complete cleanly");
      }
      // Phase 2: render every detected cut as a client clip
      setPhase("generating");
      const gen = await api.post(`/box/videos/${video.id}/generate-all-clips`);
      const clipJobId = gen.data.job_id;
      const cj = await pollClipJob(clipJobId, setClipJob);
      if (cancelledRef.current) return;
      if (cj.status !== "completed") {
        throw new Error(cj.error || "Clip generation failed");
      }
      setPhase("done");
      toast.success(`${cj.generated} clip${cj.generated === 1 ? "" : "s"} added to your Rodeo Box`);
      onDone && onDone();
    } catch (e) {
      if (cancelledRef.current) return;
      setError(e?.response?.data?.detail || e.message || "Something went wrong");
      setPhase("error");
    }
  };

  const busy = phase === "scanning" || phase === "generating";
  const shortVideo = (video.duration || 0) > 0 && (video.duration || 0) < 120;
  const scanPct = scanJob?.progress ? Math.round(scanJob.progress * 100) : 0;
  const clipPct = clipJob?.total
    ? Math.round(((clipJob.generated || 0) / clipJob.total) * 100)
    : 0;

  return (
    <div className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4" data-testid="deep-scan-modal">
      <div className="bg-stone-950 border border-stone-800 w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-stone-800">
          <div>
            <div className="label-tiny mb-1 text-orange-500">Deep Scan · Pro</div>
            <div className="font-display uppercase text-xl font-bold truncate max-w-[380px]" title={video.original_name}>
              {video.original_name || "Video"}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            className="p-2 border border-stone-700 hover:border-orange-500 text-stone-300 hover:text-orange-400 disabled:opacity-40"
            data-testid="deep-scan-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {phase === "idle" && (
            <div className="space-y-3 text-sm text-stone-300">
              {shortVideo && (
                <div className="border border-amber-800 bg-amber-900/20 p-3 text-xs" data-testid="deep-scan-short-warning">
                  <div className="flex items-start gap-2">
                    <AlertTriangle size={13} className="text-amber-400 shrink-0 mt-0.5" />
                    <div className="text-amber-200/90">
                      <div className="font-display uppercase tracking-widest text-amber-300 text-[10px] mb-1">
                        Short clip — Deep Scan may be overkill
                      </div>
                      This video is <span className="font-mono-tc">{Math.round(video.duration || 0)}s</span>. Deep Scan still takes ~1 minute
                      per run (the AI has a fixed startup cost). For clips under 2 minutes, hitting <strong>Cut</strong> and marking In/Out
                      by hand is usually faster.
                    </div>
                  </div>
                </div>
              )}
              <p>
                AI will watch your video window-by-window, spot every run / attempt automatically,
                and render each one as its own clip in your Rodeo Box.
              </p>
              <ul className="text-xs text-stone-400 space-y-1">
                <li>• Best for long reels — anything over 10 minutes.</li>
                <li>• Takes ~1 minute of processing per 10 minutes of footage.</li>
                <li>• You stay signed in — this runs in the background.</li>
              </ul>
              <button
                onClick={start}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm"
                data-testid="deep-scan-start"
              >
                <Sparkles size={13} /> {shortVideo ? "Run it anyway" : "Start deep scan"}
              </button>
            </div>
          )}

          {phase === "scanning" && (
            <PhaseRow
              label="Analyzing video"
              detail={cutsCount > 0 ? `${cutsCount} run${cutsCount === 1 ? "" : "s"} spotted so far…` : "Watching the reel…"}
              pct={scanPct}
              testId="deep-scan-progress-scan"
            />
          )}

          {(phase === "generating" || phase === "done") && (
            <>
              <PhaseRow
                label="Analyzing video"
                detail={`${cutsCount} run${cutsCount === 1 ? "" : "s"} detected`}
                pct={100}
                done
                testId="deep-scan-progress-scan"
              />
              <PhaseRow
                label="Rendering clips"
                detail={clipJob ? `${clipJob.generated || 0} of ${clipJob.total || 0} clips ready` : "Queuing…"}
                pct={phase === "done" ? 100 : clipPct}
                done={phase === "done"}
                testId="deep-scan-progress-generate"
              />
              {phase === "done" && (
                <button
                  onClick={onClose}
                  className="btn-primary w-full inline-flex items-center justify-center gap-2 text-sm"
                  data-testid="deep-scan-view-clips"
                >
                  <Check size={13} /> View my new clips
                </button>
              )}
            </>
          )}

          {phase === "error" && (
            <div className="border border-rose-900 bg-rose-900/20 p-3 text-sm text-rose-200">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-rose-400 shrink-0 mt-0.5" />
                <div>
                  <div className="font-display uppercase text-xs tracking-widest text-rose-300 mb-1">Deep scan failed</div>
                  <div className="text-xs text-rose-200/80">{error}</div>
                </div>
              </div>
              <button
                onClick={start}
                className="btn-secondary w-full mt-3 text-xs"
                data-testid="deep-scan-retry"
              >
                Try again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PhaseRow({ label, detail, pct, done, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-sm text-stone-200 flex items-center gap-2">
          {done ? <Check size={12} className="text-emerald-400" /> : <Loader2 size={12} className="animate-spin text-orange-400" />}
          {label}
        </div>
        <div className="font-mono-tc text-xs text-stone-400">{pct}%</div>
      </div>
      <div className="h-1.5 bg-stone-900 border border-stone-800 overflow-hidden">
        <div
          className={`h-full transition-all ${done ? "bg-emerald-500" : "bg-orange-500"}`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
      <div className="text-[11px] text-stone-500 mt-1">{detail}</div>
    </div>
  );
}

// Poll the ai_job until it hits a terminal state
async function pollAiJob(videoId, jobId, onTick) {
  while (true) {
    const r = await api.get(`/box/videos/${videoId}/deep-scan-status`);
    const job = r.data.job;
    onTick && onTick(job, r.data.cuts_count || 0);
    if (!job) throw new Error("Scan job disappeared");
    if (job.id !== jobId) {
      // A newer job took over — poll that one instead (shouldn't normally
      // happen because we block concurrent jobs, but be safe)
    }
    if (job.status === "completed" || job.status === "failed" || job.status === "cancelled") {
      return job;
    }
    await new Promise((res) => setTimeout(res, 2500));
  }
}

async function pollClipJob(jobId, onTick) {
  while (true) {
    const r = await api.get(`/clip-jobs/${jobId}`);
    const job = r.data;
    onTick && onTick(job);
    if (job.status === "completed" || job.status === "failed" || job.status === "cancelled") {
      return job;
    }
    await new Promise((res) => setTimeout(res, 2000));
  }
}
